import {
  clearAuthToken,
  storeAuthToken,
} from '../../domain/utils/authStorage.utils';
import { Action, AnyAction } from '../action.type';
import { AppState } from '../app.state';
import { initialUserState } from '../user/user.state';
import {
  CHANGE_PASSWORD,
  CHANGE_PASSWORD_FAILURE,
  CHANGE_PASSWORD_SUCCESS,
  EMAIL_SIGN_IN,
  EMAIL_SIGN_UP,
  GOOGLE_SIGN_IN,
  REFRESH_TOKEN_FAILURE,
  REFRESH_TOKEN_SUCCESS,
  RESTORE_AUTH_FAILURE,
  RESTORE_AUTH_FROM_STORAGE,
  RESTORE_AUTH_SUCCESS,
  SET_AUTH_ERROR,
  SIGN_IN_FAILURE,
  SIGN_IN_SUCCESS,
  SIGN_OUT,
  SIGN_OUT_FAILURE,
  SIGN_OUT_SUCCESS,
  SIGN_UP_FAILURE,
  VERIFY_TOKEN_FAILURE,
  VERIFY_TOKEN_SUCCESS,
} from './authentication.action.type';
import { initialAuthenticationState } from './authentication.state';

export const authenticationReducers = (
  state: AppState,
  action: AnyAction,
): AppState => {
  switch (action.type) {
    case GOOGLE_SIGN_IN:
    case EMAIL_SIGN_IN:
    case EMAIL_SIGN_UP:
      return signInReducer(state, action);
    case SIGN_IN_SUCCESS:
      return signInSuccessReducer(state, action);
    case SIGN_IN_FAILURE:
    case SIGN_UP_FAILURE:
      return signInFailureReducer(state, action);
    case SIGN_OUT:
      return signOutReducer(state, action);
    case SIGN_OUT_FAILURE:
      return signOutFailureReducer(state, action);
    case SIGN_OUT_SUCCESS:
      return signOutSuccessReducer(state, action);
    case VERIFY_TOKEN_SUCCESS:
      return verifyTokenSuccessReducer(state, action);
    case VERIFY_TOKEN_FAILURE:
      return verifyTokenFailureReducer(state, action);
    case REFRESH_TOKEN_SUCCESS:
      return refreshTokenSuccessReducer(state, action);
    case REFRESH_TOKEN_FAILURE:
      return refreshTokenFailureReducer(state, action);
    case RESTORE_AUTH_FROM_STORAGE:
      return restoreAuthReducer(state, action);
    case RESTORE_AUTH_SUCCESS:
      return restoreAuthSuccessReducer(state, action);
    case RESTORE_AUTH_FAILURE:
      return restoreAuthFailureReducer(state, action);
    case SET_AUTH_ERROR:
      return setAuthErrorReducer(state, action);
    case CHANGE_PASSWORD:
      return changePasswordReducer(state, action);
    case CHANGE_PASSWORD_SUCCESS:
      return changePasswordSuccessReducer(state, action);
    case CHANGE_PASSWORD_FAILURE:
      return changePasswordFailureReducer(state, action);
    default:
      return state;
  }
};

export const signInReducer = (state: AppState, _action: Action<void>) => {
  return {
    ...state,
    authentication: {
      ...state.authentication,
      isLoading: true,
    },
  };
};

export const signInSuccessReducer = (
  state: AppState,
  action: Action<{ token: string }>,
) => {
  // Store token in localStorage
  storeAuthToken(action.payload.token);

  const newState = {
    ...state,
    authentication: {
      ...state.authentication,
      isAuthenticated: true,
      token: action.payload.token,
      isLoading: false,
    },
  };
  return newState;
};

export const signInFailureReducer = (
  state: AppState,
  action: Action<string>,
): AppState => {
  const newState = {
    ...state,
    authentication: {
      ...state.authentication,
      error: action.payload,
      isLoading: false,
    },
  };
  return newState;
};

export const signOutReducer = (
  state: AppState,
  _action: Action<void>,
): AppState => {
  const newState = {
    ...state,
    authentication: {
      ...state.authentication,
      isLoading: true,
    },
  };
  return newState;
};

export const signOutSuccessReducer = (
  state: AppState,
  _action: Action<void>,
) => {
  // Clear token from localStorage
  clearAuthToken();

  const newState = {
    ...state,
    authentication: initialAuthenticationState,
    user: initialUserState,
  };
  return newState;
};

export const signOutFailureReducer = (
  state: AppState,
  action: Action<string>,
): AppState => {
  const newState = {
    ...state,
    authentication: {
      ...state.authentication,
      isLoading: false,
      error: action.payload,
    },
  };
  return newState;
};

export const verifyTokenSuccessReducer = (
  state: AppState,
  _action: Action<void>,
): AppState => {
  return {
    ...state,
    authentication: {
      ...state.authentication,
      isAuthenticated: true,
    },
  };
};

const verifyTokenFailureReducer = (
  state: AppState,
  action: Action<string>,
): AppState => {
  const newState = {
    ...state,
    authentication: {
      ...state.authentication,
      token: null,
      isAuthenticated: false,
      error: action.payload,
    },
  };
  return newState;
};

const refreshTokenSuccessReducer = (
  state: AppState,
  action: Action<string>,
): AppState => {
  // Store updated token in localStorage
  storeAuthToken(action.payload);

  const newState = {
    ...state,
    authentication: {
      ...state.authentication,
      isAuthenticated: true,
      token: action.payload,
    },
  };
  return newState;
};

const refreshTokenFailureReducer = (
  state: AppState,
  action: Action<string>,
): AppState => {
  // Clear token from localStorage on refresh failure
  clearAuthToken();

  const newState = {
    ...state,
    authentication: {
      ...state.authentication,
      token: null,
      isAuthenticated: false,
      error: action.payload,
    },
  };
  return newState;
};

const restoreAuthReducer = (
  state: AppState,
  _action: Action<void>,
): AppState => {
  return {
    ...state,
    authentication: {
      ...state.authentication,
      isLoading: true,
      error: null,
    },
  };
};

const restoreAuthSuccessReducer = (
  state: AppState,
  action: Action<{ token: string }>,
): AppState => {
  const newState = {
    ...state,
    authentication: {
      ...state.authentication,
      isAuthenticated: true,
      token: action.payload.token,
      isLoading: false,
      error: null,
    },
  };
  return newState;
};

const restoreAuthFailureReducer = (
  state: AppState,
  action: Action<string>,
): AppState => {
  // Clear token from localStorage on restore failure
  clearAuthToken();

  const newState = {
    ...state,
    authentication: {
      ...state.authentication,
      token: null,
      isAuthenticated: false,
      isLoading: false,
      error: action.payload,
    },
  };
  return newState;
};

const setAuthErrorReducer = (
  state: AppState,
  action: Action<string>,
): AppState => {
  return {
    ...state,
    authentication: {
      ...state.authentication,
      error: action.payload,
    },
  };
};

const changePasswordReducer = (
  state: AppState,
  _action: Action<void>,
): AppState => {
  return {
    ...state,
    authentication: {
      ...state.authentication,
      isLoading: true,
    },
  };
};

const changePasswordSuccessReducer = (
  state: AppState,
  action: Action<string>,
): AppState => {
  return {
    ...state,
    authentication: {
      ...state.authentication,
      token: action.payload,
      isLoading: false,
    },
  };
};

const changePasswordFailureReducer = (
  state: AppState,
  action: Action<string>,
): AppState => {
  return {
    ...state,
    authentication: {
      ...state.authentication,
      isLoading: false,
      error: action.payload,
    },
  };
};
